import React, { useContext, useEffect, useState } from 'react';
import './Payment.css';
import { useTranslation } from 'react-i18next';
import { StoreContext } from '../../Context/StoreContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';

const Payment = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const orderData = location.state?.orderData;
    const { token, url, currency, setCartItems, clearCart } = useContext(StoreContext);
    const navigate = useNavigate();
    
    const [isProcessing, setIsProcessing] = useState(false);
    const [afripayFormData, setAfripayFormData] = useState(null);

    // Redirect to cart page if no token or order data
    useEffect(() => {
        if (!token) {
            toast.error(t('payment.sign_in_to_complete'));
            navigate('/cart');
        } else if (!orderData) {
            toast.error(t('payment.no_order_information'));
            navigate('/cart');
        } else if (orderData.paymentMethod === 'cod') {
            // For COD orders, process directly without showing payment form
            handleCodPayment();
        } else if (orderData.paymentMethod === 'afripay') {
            // For Afripay orders, get the payment form data
            handleAfripayPayment();
        }
    }, [token, orderData]);

    const handleCodPayment = async () => {
        setIsProcessing(true);
        try {
            // Process the COD order directly
            const response = await axios.post(
                `${url}/api/order/place`,
                orderData,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (response.data.success) {
                await clearCart();
                toast.success(t('payment.order_placed_successfully'));
                navigate("/myorders");
            } else {
                toast.error(response.data.message || t('payment.failed_to_place_order'));
            }
        } catch (error) {
            console.error("COD payment error:", error.response?.data || error.message);
            toast.error(error.response?.data?.message || t('payment.failed_to_place_order'));
        }
        setIsProcessing(false);
    };

    const handleAfripayPayment = async () => {
        setIsProcessing(true);
        try {
            // Get Afripay form data from backend
            const response = await axios.post(
                `${url}/api/order/create-afripay-payment`,
                orderData,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (response.data.success && response.data.afripay_form_data) {
                setAfripayFormData(response.data.afripay_form_data);
            } else {
                toast.error(response.data.message || t('payment.failed_to_create_afripay'));
                navigate('/cart');
            }
        } catch (error) {
            console.error("Afripay payment error:", error.response?.data || error.message);
            toast.error(error.response?.data?.message || t('payment.failed_to_create_afripay'));
            navigate('/cart');
        }
        setIsProcessing(false);
    };

    const handleAfripaySubmit = () => {
        if (afripayFormData) {
            // Create a form and submit it to Afripay
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = 'https://www.afripay.africa/checkout/index.php';
            
            // Add all form fields
            Object.keys(afripayFormData).forEach(key => {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = key;
                input.value = afripayFormData[key];
                form.appendChild(input);
            });
            
            document.body.appendChild(form);
            form.submit();
        }
    };

    if (!orderData || isProcessing) {
        return (
            <div className="payment-page">
                <div className="payment-container">
                    <h1>{isProcessing ? t('payment.processing_order') : t('payment.loading')}</h1>
                </div>
            </div>
        );
    }

    // Don't render payment form for COD orders
    if (orderData.paymentMethod === 'cod') {
        return null;
    }

    // Render Afripay payment form
    if (orderData.paymentMethod === 'afripay' && afripayFormData) {
        return (
            <div className="payment-page">
                <div className="payment-container">
                    <h1>{t('payment.complete_your_payment')}</h1>
                    <div className="payment-details">
                        <div className="order-summary">
                            <h2>{t('payment.order_summary')}</h2>
                            <div className="summary-items">
                                {orderData.items.map((item, index) => (
                                    <div key={index} className="summary-item">
                                        <p>{item.name} x {item.quantity}</p>
                                        <p>{Math.round(item.price * item.quantity)} {currency}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="summary-totals">
                                <div className="summary-row">
                                    <p>{t('payment.subtotal')}</p>
                                    <p>{Math.round(orderData.amount - orderData.deliveryCharge)} {currency}</p>
                                </div>
                                <div className="summary-row">
                                    <p>{t('payment.delivery_fee')}</p>
                                    <p>{Math.round(orderData.deliveryCharge)} {currency}</p>
                                </div>
                                <div className="summary-row total">
                                    <p>{t('payment.total')}</p>
                                    <p>{Math.round(orderData.amount)} {currency}</p>
                                </div>
                            </div>
                        </div>
                        <div className="payment-form-container">
                            <h2>{t('payment.pay_with_afripay')}</h2>
                            <div className="afripay-payment-section">
                                <p className="payment-description">
                                    {t('payment.afripay_redirect_description')}
                                </p>
                                <div className="payment-methods">
                                    <div className="payment-method">
                                        <span className="method-name">{t('payment.lumicash')}</span>
                                    </div>
                                    <div className="payment-method">
                                        <span className="method-name">{t('payment.ecocash')}</span>
                                    </div>
                                </div>
                                <div className="payment-amount">
                                    <p>{t('payment.amount_to_pay')}: <strong>{Math.round(orderData.amount)} {currency}</strong></p>
                                </div>
                                <button 
                                    onClick={handleAfripaySubmit}
                                    className="afripay-button"
                                >
                                    <img 
                                        src="https://www.afripay.africa/logos/pay_with_afripay.png" 
                                        alt={t('payment.pay_with_afripay_alt')} 
                                        className="afripay-logo"
                                    />
                                </button>
                                <p className="security-note">
                                    {t('payment.security_note')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Fallback for any other payment method
    return (
        <div className="payment-page">
            <div className="payment-container">
                <h1>{t('payment.method_not_supported')}</h1>
                <p>{t('payment.select_valid_method')}</p>
                <button onClick={() => navigate('/cart')} className="payment-button">
                    {t('payment.go_back_to_cart')}
                </button>
            </div>
        </div>
    );
};

export default Payment; 